import { skipToken } from '@reduxjs/toolkit/query';
import {
    EnrollmentStatusIndicator,
    OnboardingStatusIndicator,
    ReferralStatusIndicator,
} from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps } from 'react';

import { ApertureReferralStatusIndicator } from 'components/shared/ApertureReferralStatusIndicator';
import { PLATFORM_ELIGIBILITY_PENDING } from 'services/memberEligibilityStatus';
import { useGetOnboardingStatusMapQuery } from 'store/api/onboardingApi';

interface ReferralAndOnboardingStatusIndicatorProps {
    status: ComponentProps<typeof ApertureReferralStatusIndicator>['status'];
    enrollmentStatus?: string;
    referralId?: string;
}

export const ReferralAndOnboardingStatusIndicator = ({
    status,
    enrollmentStatus = undefined,
    referralId = undefined,
}: ReferralAndOnboardingStatusIndicatorProps) => {
    const { payersEnhancedEligibility } = useFlags<{
        payersEnhancedEligibility: boolean;
    }>();

    const canShowEnrollmentMonitoring =
        payersEnhancedEligibility && status !== ReferralStatusIndicator.Statuses.EXPIRED;
    const isEnrollmentMonitoring = enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING;

    const canShowOnboardingStatus = status === ReferralStatusIndicator.Statuses.PHARM;

    const { data: onboardingStatusMap } = useGetOnboardingStatusMapQuery(
        canShowOnboardingStatus ? undefined : skipToken,
    );

    const onboardingStatus = referralId
        ? onboardingStatusMap?.[referralId.toLowerCase()]
        : undefined;

    if (canShowEnrollmentMonitoring && isEnrollmentMonitoring) {
        return <EnrollmentStatusIndicator status={EnrollmentStatusIndicator.Statuses.PENDING} />;
    }

    return canShowOnboardingStatus && onboardingStatus ? (
        <OnboardingStatusIndicator status={onboardingStatus} />
    ) : (
        <ApertureReferralStatusIndicator status={status} />
    );
};
